/**
 * Decide whether the current origin can satisfy the Clerk publishable key.
 *
 * A Clerk PRODUCTION instance is locked to its own app domain: it only accepts
 * a request whose Origin is that domain or a subdomain of it. When the built
 * dashboard bundle is served on any other origin -- the team-review proxy
 * renders it on the Railway proxy host, a different registrable domain -- the
 * clerk-js handshake can never succeed. The failure is STRUCTURAL, not a
 * transient network problem, so the loading screen waits, reports a fake
 * auth_load_failed, and offers a Retry that can never work. This helper spots
 * that case up front so the app can mount stubs and show honest copy instead.
 *
 * SAFETY. A false positive would block real sign-in on app.overlayqa.com, so
 * this fails OPEN: it returns true ONLY when it is confident the origin cannot
 * satisfy the key, and returns false on every ambiguous input.
 */
import { resolveClerkFrontendHost } from './clerkFrontendHost';

/** Only production instances are origin-locked. */
const PROD_KEY_PREFIX = 'pk_live_';

/**
 * The app domain a production frontend host authenticates. Clerk's production
 * Frontend API host is `clerk.<app-domain>`, so the app domain is the host with
 * its first label removed: `clerk.overlayqa.com` -> `overlayqa.com`.
 *
 * @returns the app domain, or null when the host is not a `clerk.<domain>` shape
 *   with at least two remaining labels -- in which case the caller must fail
 *   open rather than guess a domain to block against.
 */
function clerkAppDomain(frontendHost: string): string | null {
  const firstDot = frontendHost.indexOf('.');
  if (firstDot < 1) return null;
  const domain = frontendHost.slice(firstDot + 1).toLowerCase();
  // Need a registrable domain (at least two labels) to trust the comparison.
  if (domain.split('.').length < 2) return null;
  return domain;
}

/**
 * @returns true only when a production key is served on an origin outside its
 *   app domain. Every other case (dev key, absent/malformed key, missing
 *   hostname, host under the app domain) returns false.
 */
export function isClerkOriginBlocked(
  publishableKey: string | undefined | null,
  hostname: string | undefined | null,
): boolean {
  const key = (publishableKey ?? '').trim();
  if (!key.startsWith(PROD_KEY_PREFIX)) return false;

  const host = (hostname ?? '').trim().toLowerCase();
  if (!host) return false;

  const frontendHost = resolveClerkFrontendHost(key);
  if (!frontendHost) return false;

  const appDomain = clerkAppDomain(frontendHost);
  if (!appDomain) return false;

  if (host === appDomain) return false;
  if (host.endsWith('.' + appDomain)) return false;

  return true;
}
